import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Upload, X, Plus, Loader2, ArrowLeft } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";

const CATEGORIES = ["Sofa", "Chair", "Table", "Bed", "Wardrobe", "Decor", "Lighting", "Curtain", "Tile", "Paint", "Other"];
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const FACINGS = [
  { value: "front", label: "Front" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];

// Auto-generated alternate-angle views the backend appends to images[] after
// background removal finishes. Client-side stopping heuristic only — not
// authoritative, the backend may append fewer.
const EXPECTED_VIEWS = { Sofa: 3, Bed: 3, Wardrobe: 3, Chair: 3, Table: 3 };
const expectedViewCount = (category) => EXPECTED_VIEWS[category] || 1;
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 90000;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const enhancingIdsRef = useRef(new Set());
  const [enhancingIds, setEnhancingIds] = useState(new Set());
  const pollingIdsRef = useRef(new Set());
  const [pollingIds, setPollingIds] = useState(new Set());
  const pollTimeoutsRef = useRef({});

  const setEnhancing = useCallback((id, val) => {
    if (val) enhancingIdsRef.current.add(id);
    else enhancingIdsRef.current.delete(id);
    setEnhancingIds(new Set(enhancingIdsRef.current));
  }, []);

  const setPolling = useCallback((id, val) => {
    if (val) pollingIdsRef.current.add(id);
    else pollingIdsRef.current.delete(id);
    setPollingIds(new Set(pollingIdsRef.current));
  }, []);

  // Extra alternate-angle views land silently in the DB after the primary
  // clean-image WS event resolves — there's no push notification for them,
  // so we poll the product list briefly to pick them up.
  const pollForVariants = useCallback((productId, category) => {
    if (!productId || pollingIdsRef.current.has(productId)) return;
    const expected = expectedViewCount(category);
    setPolling(productId, true);
    let elapsed = 0;

    const stop = () => {
      delete pollTimeoutsRef.current[productId];
      setPolling(productId, false);
    };

    const tick = () => {
      api.get("/products").then((r) => {
        const match = r.data?.find((p) => p.id === productId);
        if (match?.images?.length) {
          setProducts((prev) =>
            prev.map((p) => p.id === productId ? { ...p, images: match.images, image_url: match.image_url } : p)
          );
          if (match.images.length >= expected) return stop();
        }
        elapsed += POLL_INTERVAL_MS;
        if (elapsed >= POLL_TIMEOUT_MS) return stop();
        pollTimeoutsRef.current[productId] = setTimeout(tick, POLL_INTERVAL_MS);
      }).catch(() => {
        elapsed += POLL_INTERVAL_MS;
        if (elapsed >= POLL_TIMEOUT_MS) return stop();
        pollTimeoutsRef.current[productId] = setTimeout(tick, POLL_INTERVAL_MS);
      });
    };

    pollTimeoutsRef.current[productId] = setTimeout(tick, POLL_INTERVAL_MS);
  }, [setPolling]);

  useEffect(() => {
    const timeouts = pollTimeoutsRef.current;
    return () => { Object.values(timeouts).forEach(clearTimeout); };
  }, []);

  const load = useCallback(() => {
    api
      .get("/products", { params: { category: category === "All" ? undefined : category, q: query || undefined } })
      .then((r) => setProducts(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load products"));
  }, [category, query]);

  const watchingRef = useRef(new Set());

  const watchCleaningProduct = useCallback((product) => {
    if (!product?.cleaning || !product?.id) return;
    if (watchingRef.current.has(product.id)) return;
    watchingRef.current.add(product.id);
    const jobId = product.clean_job_id || product.id;
    const wsBase = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws");
    if (!wsBase) {
      watchingRef.current.delete(product.id);
      return;
    }
    const ws = new WebSocket(`${wsBase}/api/ws/${jobId}`);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        ws.close();
        watchingRef.current.delete(product.id);
        if (msg.status === "done" && msg.url) {
          setProducts((prev) =>
            prev.map((p) => p.id === product.id ? { ...p, image_url: msg.url, images: [{ id: p.id, url: msg.url, label: "" }], cleaning: false } : p)
          );
          toast.success("Background removed", { icon: "✨" });
          pollForVariants(product.id, product.category);
        } else {
          setProducts((prev) =>
            prev.map((p) => p.id === product.id ? { ...p, cleaning: false } : p)
          );
        }
      } catch (_) {}
    };
    ws.onerror = () => { ws.close(); watchingRef.current.delete(product.id); };
    ws.onclose = () => { watchingRef.current.delete(product.id); };
  }, [pollForVariants]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    products.forEach((p) => { if (p.cleaning) watchCleaningProduct(p); });
  }, [products, watchCleaningProduct]); // eslint-disable-line



  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="overline">Catalog</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">Product Library</h1>
          <p className="mt-2 text-[#5C5F62]">Everything customers can visualize in their room.</p>
        </div>
        <button data-testid="add-product-btn" onClick={() => setShowAdd(true)} className="neivai-btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2">
          <Plus className="h-4 w-4" /> Add product
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`category-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm border transition ${category === c ? "bg-[#1A1C1E] text-white border-[#1A1C1E]" : "border-[#E5E5E5] text-[#5C5F62] hover:border-[#1A1C1E]"}`}
            >{c}</button>
          ))}
        </div>
        <input
          data-testid="products-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name…"
          className="md:ml-auto md:w-72 bg-white border border-[#E5E5E5] rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]"
        />
      </div>

      {products.length === 0 ? (
        <div className="neivai-card p-12 text-center">
          <p className="text-[#8A8D91]">No products yet. Click &ldquo;Add product&rdquo; to upload your first item.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} p={p} enhancingIds={enhancingIds} pollingIds={pollingIds} onEdit={setEditing} onRemove={remove} />
          ))}
        </div>
      )}

      {showAdd && <AddProductDialog onClose={() => setShowAdd(false)} onSaved={(saved) => {
        const { _enhancing, _temp, _replaceTemp, _remove, _startPolling, ...product } = saved;
        if (_remove) {
          // Upload failed — remove optimistic card
          setProducts((prev) => prev.filter((p) => p.id !== product.id));
        } else if (_temp) {
          // Optimistic: add temp card immediately, close dialog
          setShowAdd(false);
          if (_enhancing) setEnhancing(product.id, true);
          setProducts((prev) => [product, ...prev]);
        } else if (_replaceTemp) {
          // Real save done — replace temp card with real product. Spinner
          // responsibility now belongs to `product.cleaning` (server truth,
          // surfaced via watchCleaningProduct's WebSocket watch below), so
          // drop the optimistic enhancing flag rather than transfer it.
          setEnhancing(_replaceTemp, false);
          setProducts((prev) => prev.map((p) => p.id === _replaceTemp ? product : p));
        } else if (_startPolling) {
          // Primary photo is settled (enhanced or not) — pick up any
          // alternate-angle views the backend appends afterward.
          pollForVariants(product.id, product.category);
        } else if (enhancingIdsRef.current.has(product.id)) {
          // Clean finished — update image, remove overlay
          setEnhancing(product.id, false);
          setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, ...product } : p));
        } else {
          setShowAdd(false);
          load();
        }
      }} />}
      {editing && <EditProductDialog product={editing} onClose={() => setEditing(null)} onSaved={(updated) => { setEditing(null); setProducts((prev) => prev.map((p) => p.id === updated.id ? updated : p)); }} />}
    </div>
  );
}

function ProductCard({ p, enhancingIds, pollingIds, onEdit, onRemove }) {
  const [activeUrl, setActiveUrl] = useState(p.image_url);

  useEffect(() => { setActiveUrl(p.image_url); }, [p.image_url]);

  return (
    <div className="neivai-card overflow-hidden group" data-testid={`product-card-${p.id}`}>
      <div className="aspect-square bg-[#F3F1EC] overflow-hidden relative">
        <img
          src={resolveMediaUrl(activeUrl)}
          alt={p.name}
          className="h-full w-full object-cover group-hover:scale-105 transition duration-500"
          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling?.classList.remove("hidden"); }}
        />
        <div className="hidden absolute inset-0 flex items-center justify-center bg-[#F3F1EC] text-[#8A8D91] text-xs text-center px-2">No image</div>
        {(p.cleaning || enhancingIds.has(p.id)) && (
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
            <span className="text-white text-[10px] font-medium">Enhancing…</span>
          </div>
        )}
        {p.images?.length > 1 && !p.cleaning && (
          <span className="absolute bottom-2 right-2 bg-[#1A1C1E]/70 text-white text-[10px] rounded-full px-2 py-0.5">
            {p.images.length} variants
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="text-sm font-medium truncate">{p.name}</h4>
            <p className="text-xs text-[#8A8D91]">{p.category}{p.price ? ` · ₹${p.price}` : ""}</p>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
            <button data-testid={`edit-${p.id}`} onClick={() => onEdit(p)} className="p-1.5 rounded hover:bg-[#F3F1EC]"><Pencil className="h-3.5 w-3.5" /></button>
            <button data-testid={`delete-${p.id}`} onClick={() => onRemove(p.id)} className="p-1.5 rounded hover:bg-[#F3F1EC] text-[#B74F4F]"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
        {p.images?.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`product-variants-${p.id}`}>
            {p.images.map((img, idx) => (
              <img
                key={img.id || idx}
                src={resolveMediaUrl(img.url)}
                alt={img.label || `${p.name} variant ${idx + 1}`}
                title={img.label || undefined}
                onClick={() => setActiveUrl(img.url)}
                className={`h-8 w-8 object-cover rounded cursor-pointer transition ${
                  activeUrl === img.url ? "ring-2 ring-[#C86B53]" : "border border-[#E5E5E5] hover:ring-2 hover:ring-[#C86B53]/50"
                }`}
                onError={(e) => { e.target.style.opacity = "0"; }}
              />
            ))}
          </div>
        )}
        {pollingIds.has(p.id) && (
          <p className="mt-2 text-[10px] text-[#8A8D91] flex items-center gap-1">
            <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating more views…
          </p>
        )}
      </div>
    </div>
  );
}

/** Hex color chips + add control, shared by the Add and Edit product
 * dialogs. Lets a shopper later pick one of these swatches in the
 * visualizer (any product's color_palette works there) — for a Paint
 * product specifically, the chosen hex is what gets painted onto the
 * wall/ceiling. */
function ColorPaletteEditor({ colors, onAdd, onRemove }) {
  const [input, setInput] = useState("#");

  const add = () => {
    const hex = input.trim();
    if (!HEX_RE.test(hex)) return toast.error("Enter a valid hex color, e.g. #3B5D50");
    if (colors.includes(hex)) return setInput("#");
    onAdd(hex);
    setInput("#");
  };

  return (
    <div>
      <p className="text-xs text-[#8A8D91] mb-1.5">
        Color palette <span className="text-[#B7B7B7]">— colors shoppers can pick{"; "}for Paint, this is the color applied to the wall/ceiling</span>
      </p>
      {colors.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {colors.map((hex) => (
            <div key={hex} className="relative h-8 w-8 shrink-0">
              <div className="h-full w-full rounded-full border border-[#E5E5E5]" style={{ background: hex }} title={hex} />
              <button
                type="button"
                onClick={() => onRemove(hex)}
                className="absolute -top-1 -right-1 bg-[#1A1C1E] text-white rounded-full p-0.5"
              >
                <X className="h-2 w-2" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="color"
          value={HEX_RE.test(input) ? input : "#C86B53"}
          onChange={(e) => setInput(e.target.value)}
          className="h-9 w-9 rounded border border-[#E5E5E5] cursor-pointer p-0.5 shrink-0"
          title="Pick a color"
        />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="#3B5D50"
          className="flex-1 min-w-0 bg-white border border-[#E5E5E5] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]"
        />
        <button type="button" onClick={add} className="px-3 py-2 rounded-md border border-[#E5E5E5] text-sm hover:border-[#C86B53] shrink-0">
          Add
        </button>
      </div>
    </div>
  );
}

function AddProductDialog({ onClose, onSaved }) {
  const fileInput = useRef(null);
  const extraFileInput = useRef(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Sofa");
  const [price, setPrice] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [enhanceImage, setEnhanceImage] = useState(true);
  const [provideOwnViews, setProvideOwnViews] = useState(false);
  const [facing, setFacing] = useState("front");
  const [colorPalette, setColorPalette] = useState([]);
  const [primaryFile, setPrimaryFile] = useState(null);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extraImages, setExtraImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [extraBusy, setExtraBusy] = useState(false);

  const selectPrimary = (file) => {
    setPrimaryFile(file);
    setPrimaryImage({ preview: URL.createObjectURL(file) });
  };

  const removePrimary = () => {
    setPrimaryFile(null);
    setPrimaryImage(null);
  };

  const uploadExtra = async (file) => {
    setExtraBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtraImages((prev) => [...prev, { url: data.url }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setExtraBusy(false);
    }
  };

  const removeExtra = (idx) => setExtraImages((prev) => prev.filter((_, i) => i !== idx));



  const save = async () => {
    if (!name || !primaryFile) return toast.error("Name and image required");

    // Optimistic: close dialog and show card immediately with local preview
    const tempId = `temp-${Date.now()}`;
    const previewUrl = URL.createObjectURL(primaryFile);
    onSaved({
      id: tempId, name, category, price: price ? Number(price) : null,
      image_url: previewUrl, images: [{ id: tempId, url: previewUrl, label: "" }],
      _enhancing: enhanceImage, _temp: true,
    });

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", primaryFile);
      const { data: uploadData } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = uploadData.url;

      const payload = {
        name, category,
        price: price ? Number(price) : null,
        dimensions,
        color_palette: colorPalette,
        extra_image_urls: extraImages.map((i) => i.url),
        enhance: enhanceImage,
        provide_own_views: provideOwnViews,
        ...(provideOwnViews ? {} : { facing }),
      };
      const { data: saved } = await api.post(`/products?image_url=${encodeURIComponent(imageUrl)}`, payload);
      toast.success("Product added");
      // Replace temp card with real saved product. Do NOT clean the image
      // ourselves here — the backend already runs background removal (when
      // enhance=true) followed by alternate-angle view generation as one
      // sequenced pipeline (see services/product_views.py), and notifies us
      // over WebSocket when the primary photo is done via `cleaning` below.
      // Doing our own /uploads/clean + PATCH here used to race that
      // pipeline: the PATCH replaced images[] wholesale and could land
      // right in the middle of the backend appending mirrored/generated
      // views, wiping them back down to a single photo.
      onSaved({ ...saved, _replaceTemp: tempId });

      if (!saved.cleaning) {
        // Primary photo is already final (enhance=false) — alternate views
        // (if any) can start arriving right away.
        onSaved({ ...saved, _startPolling: true });
      }
      // If saved.cleaning is true, the `products.forEach` watcher below
      // picks it up via watchCleaningProduct, which listens over WebSocket
      // and starts polling for extra views itself once cleaning finishes.
    } catch (err) {
      onSaved({ id: tempId, _remove: true });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.msg || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C1E]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="shrink-0 px-4 py-3 sm:p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC] sm:hidden"><ArrowLeft className="h-4 w-4" /></button>
            <h3 className="text-lg sm:text-2xl font-display font-medium">Add product</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC] hidden sm:block"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 sm:p-6 flex flex-col sm:grid sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Image upload */}
          <div className="min-w-0">
            {!primaryImage ? (
              <div
                onClick={() => fileInput.current?.click()}
                className="h-36 sm:h-auto sm:aspect-square rounded-lg border-2 border-dashed border-[#E5E5E5] flex items-center justify-center transition bg-[#FAFAFA] cursor-pointer hover:border-[#C86B53]"
                data-testid="product-image-dropzone"
              >
                <div className="text-center text-[#8A8D91]">
                  <Upload className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-sm">Tap to upload image</p>
                  <p className="text-xs mt-1">JPG · PNG · WEBP</p>
                </div>
              </div>
            ) : (
              <>
                <div className="h-36 sm:h-auto sm:aspect-square rounded-lg overflow-hidden bg-[#FAFAFA] relative">
                  <img src={primaryImage.preview || resolveMediaUrl(primaryImage.url)} alt="preview" className="h-full w-full object-cover" />
                  {uploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    </div>
                  )}
                  <button type="button" onClick={removePrimary} className="absolute top-2 right-2 bg-[#1A1C1E]/70 text-white rounded-full p-1">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-xs text-[#8A8D91] mt-2 mb-1">Variants <span className="text-[#B7B7B7]">— e.g. other colors or designs</span></p>
                <div className="flex flex-wrap gap-2">
                  {extraImages.map((img, idx) => (
                    <div key={idx} className="relative h-12 w-12 shrink-0">
                      <img src={resolveMediaUrl(img.url)} alt={`variant ${idx + 1}`} className="h-full w-full object-cover rounded-md border border-[#E5E5E5]" onError={(e) => { e.target.style.opacity = "0"; }} />
                      <button type="button" onClick={() => removeExtra(idx)} className="absolute -top-1.5 -right-1.5 bg-[#1A1C1E] text-white rounded-full p-0.5">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" data-testid="add-variant-image-btn" disabled={extraBusy} onClick={() => extraFileInput.current?.click()} className="h-12 w-12 shrink-0 rounded-md border-2 border-dashed border-[#E5E5E5] flex items-center justify-center hover:border-[#C86B53] transition disabled:opacity-60">
                    {extraBusy ? <Loader2 className="h-4 w-4 text-[#C86B53] animate-spin" /> : <Plus className="h-4 w-4 text-[#8A8D91]" />}
                  </button>
                </div>
              </>
            )}
            <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) selectPrimary(e.target.files[0]); e.target.value = ""; }} />
            <input ref={extraFileInput} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadExtra(e.target.files[0]); e.target.value = ""; }} />
          </div>
          {/* Fields */}
          <div className="space-y-3 min-w-0">
            <input data-testid="product-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="product-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" placeholder="Price (INR)" className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]" />
            </div>
            <input value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="Dimensions e.g. 200 x 90 x 85 cm" className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm" />
            <ColorPaletteEditor
              colors={colorPalette}
              onAdd={(hex) => setColorPalette((prev) => [...prev, hex])}
              onRemove={(hex) => setColorPalette((prev) => prev.filter((c) => c !== hex))}
            />
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input type="checkbox" checked={enhanceImage} onChange={(e) => setEnhanceImage(e.target.checked)} className="w-4 h-4 rounded border-[#E5E5E5] accent-[#C86B53]" />
              <span className="text-sm text-[#1A1C1E]">Enhance image (remove background)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="provide-own-views-checkbox" checked={provideOwnViews} onChange={(e) => setProvideOwnViews(e.target.checked)} className="w-4 h-4 rounded border-[#E5E5E5] accent-[#C86B53]" />
              <span className="text-sm text-[#1A1C1E]">I&apos;ll upload other angle photos myself</span>
            </label>
            {!provideOwnViews && (
              <div>
                <p className="text-xs text-[#8A8D91] mb-1.5">Which side does this photo show?</p>
                <div className="flex gap-2">
                  {FACINGS.map((f) => (
                    <button key={f.value} type="button" data-testid={`facing-${f.value}`} onClick={() => setFacing(f.value)}
                      className={`flex-1 py-1.5 rounded-full text-xs border transition ${facing === f.value ? "bg-[#1A1C1E] text-white border-[#1A1C1E]" : "border-[#E5E5E5] text-[#5C5F62] hover:border-[#1A1C1E]"}`}
                    >{f.label}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 px-4 py-3 sm:p-6 border-t border-[#E5E5E5] flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <button onClick={onClose} className="neivai-btn-secondary w-full sm:w-auto">Cancel</button>
          <button data-testid="product-save-btn" onClick={save} disabled={busy || !primaryFile || !name} className="neivai-btn-primary disabled:opacity-60 w-full sm:w-auto">
            {busy ? "Saving…" : "Save product"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProductDialog({ product, onClose, onSaved }) {
  const extraFileInput = useRef(null);
  const [name, setName] = useState(product.name);
  const [category, setCategory] = useState(product.category);
  const [price, setPrice] = useState(product.price || "");
  const [images, setImages] = useState(
    product.images?.length ? product.images : [{ id: "primary", url: product.image_url }]
  );
  const [dimensions, setDimensions] = useState(product.dimensions || "");
  const [colorPalette, setColorPalette] = useState(product.color_palette || []);
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);

  const addImage = async (file) => {
    setImageBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.post(`/products/${product.id}/images`, null, {
        params: { image_url: data.url },
      });
      setImages(updated.images?.length ? updated.images : images);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to add image");
    } finally {
      setImageBusy(false);
    }
  };

  const removeImage = async (imageId) => {
    if (images.length <= 1) return toast.error("A product needs at least one image");
    setImageBusy(true);
    try {
      const { data: updated } = await api.delete(`/products/${product.id}/images/${imageId}`);
      setImages(updated.images?.length ? updated.images : images);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove image");
    } finally {
      setImageBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data: updated } = await api.patch(`/products/${product.id}`, { name, category, price: price ? Number(price) : null, dimensions, color_palette: colorPalette });
      toast.success("Product updated");
      onSaved(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.msg) {
        toast.error(detail.msg);
      } else {
        toast.error("Failed to update");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C1E]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="shrink-0 px-4 py-3 sm:p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="text-lg sm:text-2xl font-display font-medium">Edit product</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 sm:p-6 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" placeholder="Price" className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm" />
          </div>
          <input value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="Dimensions e.g. 200 x 90 x 85 cm" className="w-full bg-white border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm" />
          <ColorPaletteEditor
            colors={colorPalette}
            onAdd={(hex) => setColorPalette((prev) => [...prev, hex])}
            onRemove={(hex) => setColorPalette((prev) => prev.filter((c) => c !== hex))}
          />
          <div>
            <p className="text-xs text-[#8A8D91] mb-2">Variants <span className="text-[#B7B7B7]">— other colors or designs</span></p>
            <div className="flex flex-wrap gap-2">
              {images.map((img, idx) => (
                <div key={img.id || idx} className="relative h-12 w-12 shrink-0">
                  <img src={resolveMediaUrl(img.url)} alt={`variant ${idx + 1}`} className={`h-full w-full object-cover rounded-md ${idx === 0 ? "ring-2 ring-[#C86B53]" : "border border-[#E5E5E5]"}`} onError={(e) => { e.target.style.opacity = "0"; }} />
                  {images.length > 1 && (
                    <button type="button" disabled={imageBusy} onClick={() => removeImage(img.id)} className="absolute -top-1.5 -right-1.5 bg-[#1A1C1E] text-white rounded-full p-0.5 disabled:opacity-60">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" data-testid="add-variant-image-btn" disabled={imageBusy} onClick={() => extraFileInput.current?.click()} className="h-12 w-12 shrink-0 rounded-md border-2 border-dashed border-[#E5E5E5] flex items-center justify-center hover:border-[#C86B53] transition disabled:opacity-60">
                {imageBusy ? <Loader2 className="h-4 w-4 text-[#C86B53] animate-spin" /> : <Plus className="h-4 w-4 text-[#8A8D91]" />}
              </button>
            </div>
            <input ref={extraFileInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && addImage(e.target.files[0])} />
          </div>
        </div>
        <div className="shrink-0 px-4 py-3 sm:p-6 border-t border-[#E5E5E5] flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <button onClick={onClose} className="neivai-btn-secondary w-full sm:w-auto">Cancel</button>
          <button onClick={save} disabled={busy} className="neivai-btn-primary disabled:opacity-60 w-full sm:w-auto">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}